<template>
  <div v-if="visibleItems.length" :class="classes" class="menu">
    <div
      class="menu-head"
      :class="{ 'menu-head--toggle': collapsable }"
      @click="collapsable && toggle()"
    >
      <div v-if="title" class="menu-title" v-html="title"></div>
      <CygnusIcon
        v-if="collapsable"
        name="chevron-up"
        variant="solid"
        class="toggle-nav-items"
        :class="{ collapsed: isCollapsed }"
      />
    </div>

    <template v-if="!isCollapsed">
      <div
        v-for="item in visibleItems"
        :key="item.href || item.name"
        :class="`group group-${groupSlug(item)}`"
      >
        <!-- Items with children keep the vendor renderer (bootstrap collapse,
             three nesting levels). Only leaf items are rendered here, because
             only a leaf can carry a count badge — and every Goshawk nav entry
             is deliberately flat (see observabilityModule::menuItems()). -->
        <NavList v-if="hasChildren(item)" :group="item" />
        <div v-else class="menu-group">
          <a v-if="isExternal(item)" :href="item.href" @click="$emit('click')">
            <CygnusIcon v-if="item.icon" class="mr-1" :name="item.icon" />
            <span v-html="item.title"></span>
          </a>
          <router-link v-else :to="item.href" @click="checkReload(item.href)">
            <CygnusIcon v-if="item.icon" class="mr-1" :name="item.icon" />
            <span v-html="item.title"></span>
            <span
              v-if="badgeFor(item) !== null"
              class="nav-count"
              :class="badgeClass(item)"
            >{{ badgeFor(item) }}</span>
          </router-link>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
/**
 * Goshawk's left-nav menu renderer — PROJECT-tier override of icarus-core's
 * `nav/Menu.vue` (registered by PascalCase basename, so this file replaces the
 * core one app-wide; see `icarus/src/index.js`'s three-tier registration).
 *
 * It lives under `src/components/`, NOT `src/themes/base/`, and must stay
 * there. The theme tier is gated on the `ICARUS_THEME` build env, which is
 * fed only by the gitignored `icarus/.env` — neither `.gitlab-ci.yml`'s
 * `icarus` job nor the on-prem `Dockerfile` sets it, so `ICARUS_THEME` is
 * always `''` in a real build and `activeThemeKeys()` returns nothing. A
 * component placed in `src/themes/` therefore compiles, ships, and silently
 * never registers. This file shipped from there in v0.9.3 and the entire
 * grouped-sidebar/badge feature was dead in production as a result.
 *
 * The 2026-08-01 UI review asked for three things the core component cannot
 * do, and this override exists for exactly those three and nothing else — the
 * search-filter behaviour, the `showMenuHeader` guard and the markup the
 * sidebar SCSS targets (`.group`, `.menu-group`, `.menu-title`) are all
 * preserved verbatim:
 *
 *  1. **Live count badges.** "Alerts carries a live count so the sidebar
 *     itself is a monitor." Which route carries which count is declared
 *     server-side (`RegisterGoshawkNavGroupsHook::BADGES`) and arrives as the
 *     `badges` prop; this component only resolves a dotted path against the
 *     shared summary payload. Nothing about the fleet is hardcoded here.
 *  2. **A default-collapsed group.** Core's `isCollapsed` always starts
 *     `false`; the System group must start closed ("still one click away, no
 *     longer competing").
 *  3. **Remembered collapse state.** An operator who opens System should find
 *     it open on the next navigation — and icarus remounts this component on
 *     every navigation, so "remembered" has to mean persisted, not just
 *     component state.
 *
 * Persistence uses the project's guarded-localStorage idiom and key
 * convention (`goshawk.<area>.<thing>`) from `services/installerStatus.js`,
 * rather than pinia-plugin-persistedstate: there is no store here to persist,
 * and a single boolean map does not justify creating one.
 */
import { computed, onMounted, ref } from 'vue';
import Mark from 'mark.js';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';
import NavList from '@apexl/icarus-core/src/components/nav/NavList.vue';
import CygnusIcon from '@apexl/icarus-core/src/components/icons/CygnusIcon.vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchOperationsSummary, peekOperationsSummary, readPath } from '@root/services/operationsSummary';

const STORAGE_KEY = 'goshawk.nav.collapsedGroups';

const props = defineProps({
  ...commonProps,
  title: { type: String, default: '' },
  menuItems: { type: Array, default: () => [] },
  search: { type: String, default: '' },
  collapsable: { type: Boolean, default: false },
  /** Initial state only — a stored preference always wins over this. */
  defaultCollapsed: { type: Boolean, default: false },
  /** Stable key for the persisted state; falls back to the title. */
  groupKey: { type: String, default: '' },
  /** `{ '<href>': { path: 'alerts.open', tone: 'alert'|'neutral' } }` */
  badges: { type: Object, default: () => ({}) },
});

defineEmits(['click']);

const route = useRoute();
const router = useRouter();

const storageKey = computed(() => props.groupKey || props.title.toLowerCase());

// ── collapse state ────────────────────────────────────────────────────────

function readCollapsedMap() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Private-browsing / storage-disabled / corrupt JSON — the nav must still
    // render, it just forgets between navigations.
    return {};
  }
}

function writeCollapsed(key, collapsed) {
  try {
    const map = readCollapsedMap();
    map[key] = collapsed;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore — see readCollapsedMap */
  }
}

const stored = readCollapsedMap()[storageKey.value];
const isCollapsed = ref(typeof stored === 'boolean' ? stored : props.defaultCollapsed);

function toggle() {
  isCollapsed.value = !isCollapsed.value;
  writeCollapsed(storageKey.value, isCollapsed.value);
}

// ── badges ────────────────────────────────────────────────────────────────

// Seeded from the shared cache so a navigation-driven remount paints the
// counts it already knows instead of flashing empty, then refreshed.
const summary = ref(peekOperationsSummary());

onMounted(async () => {
  const hasBadges = Object.keys(props.badges || {}).length > 0;
  if (!hasBadges) {
    return; // groups with no badged item never trigger a request
  }
  summary.value = await fetchOperationsSummary();
});

function badgeFor(item) {
  const spec = props.badges?.[item.href];
  if (!spec || !summary.value) {
    return null;
  }
  const value = readPath(summary.value, spec.path);

  // A genuine zero IS shown for an urgent counter — "Alerts 0" is
  // reassurance, and hiding it would make "no badge" ambiguous between
  // "nothing wrong" and "not loaded". A zero on a neutral inventory counter
  // (no servers, no domains) is noise, so it's suppressed.
  if (typeof value !== 'number') {
    return null;
  }
  if (value === 0 && spec.tone !== 'alert') {
    return null;
  }

  return value;
}

function badgeClass(item) {
  const spec = props.badges?.[item.href];
  const value = badgeFor(item);

  return {
    'nav-count--urgent': spec?.tone === 'alert' && typeof value === 'number' && value > 0,
  };
}

// ── item list (core behaviour, preserved) ─────────────────────────────────

const visibleItems = computed(() => props.menuItems
  .filter((item) => {
    // `showMenuHeader` in core: `typeof group.links !== 'undefined'`. Kept so
    // a malformed item can't render as an empty row.
    if (typeof item.links === 'undefined') {
      return false;
    }
    if (!props.search) {
      return true;
    }

    return props.search.split(' ').every(
      (chunk) => item.title.toLowerCase().includes(chunk.toLowerCase()),
    );
  })
  .map((item) => {
    if (!props.search) {
      return item;
    }
    const { ...copy } = item;
    const span = document.createElement('span');
    span.innerHTML = copy.title;
    new Mark(span).mark(props.search.split(' '), { element: 'strong' });
    copy.title = span.innerHTML;

    return copy;
  }));

function hasChildren(item) {
  return Array.isArray(item.links) && item.links.length > 0;
}

function isExternal(item) {
  return typeof item.href === 'string' && /^https?:\/\//.test(item.href);
}

function groupSlug(item) {
  return String(item.title || '').toLowerCase().replace(/<[^>]*>/g, '').replace(/\s+/g, '-');
}

/** Core `NavList` behaviour: clicking the already-active route reloads it. */
function checkReload(href) {
  if (href === route.path) {
    router.go();
  }
}
</script>

<style lang="scss">
.menu {
  .menu-head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.25rem;
    padding-right: 1rem;
    width: 100%;

    &--toggle {
      cursor: pointer;

      &:hover .menu-title {
        opacity: 0.85;
      }
    }
  }

  .toggle-nav-items {
    font-size: 0.8rem;
    color: var(--theme-text-sidebar);
    opacity: 0.6;
    cursor: pointer;
    transition: transform 0.2s ease-in-out;

    &.collapsed {
      transform: rotate(-180deg);
    }
  }

  // Count badge. Neutral by default (inventory counts are context, not
  // urgency); `--urgent` is reserved for a non-zero open-alert count, which
  // is the one number that should pull the eye to the sidebar.
  .nav-count {
    margin-left: auto;
    font-family: var(--theme-font-mono);
    font-size: 0.66rem;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    padding: 0.2rem 0.4rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    color: var(--theme-text-sidebar);

    &--urgent {
      background: var(--theme-status-danger);
      color: #fff;
      font-weight: 700;
    }
  }

  // The badge needs the link to be a row, not a block — matched to the
  // sidebar's existing `.group a` padding/typography, which is otherwise
  // untouched (see themes/base/_sidebar.scss).
  .menu-group > a {
    display: flex !important;
    align-items: center;
  }
}
</style>
